import { Signaling } from './Signaling';
import { ServerTransfer } from './ServerTransfer';
import CommonUtils from '../Utils/commonUtils';
import { Subscription } from 'rxjs';
import { getMimeType } from '../Utils/heicConverter';
import { WebRTCConnectionFactory } from '../Utils/WebRTCFactory';
import { log, warn, error } from '../Utils/logger';

const CHUNK_SIZE = 16384 * 2; // 32KB
const MAX_BUFFER_AMOUNT = 1024 * 1024; // 1MB
const BUFFER_THRESHOLD = 512 * 1024; // 512KB

export interface FileMetadata {
    uploadPending?: boolean;
    id: number;
    name: string;
    size: number;
    mimeType: string;
}

interface TransferState {
    metadata: FileMetadata;
    receivedBuffers: ArrayBuffer[];
    receivedSize: number;
    hasReportedError: boolean;
}

export class FileReceiver {
    private server: ServerTransfer;
    private disposed = false;
    public isServerMode() {
        return this.signaling.serverEnabled;
    }
    private peerConnection!: RTCPeerConnection;
    private transfers = new Map<number, TransferState>();

    private onProgress:
        | ((id: number, progress: number, bytesReceived: number) => void)
        | null = null;
    private onMetadata: ((id: number, metadata: FileMetadata) => void) | null =
        null;
    private onFileReceived: ((id: number, file: File) => void) | null = null;
    private onError: ((id: number | null, error: Error) => void) | null = null;
    private onConnectionStateChange:
        | ((state: RTCPeerConnectionState) => void)
        | null = null;

    private dataChannel: RTCDataChannel | null = null;
    private sendChannel: RTCDataChannel | null = null;
    private signaling: Signaling;

    private onSendProgress:
        | ((id: number, progress: number, bytesSent: number) => void)
        | null = null;
    private cancelledTransfers = new Set<number>();
    private bufferWaiters: Array<{
        resolve: () => void;
        reject: (reason: Error) => void;
    }> = [];

    private processedCandidates = new Set<string>();
    private candidateQueue: RTCIceCandidate[] = [];

    private key: CryptoKey;
    private subscriptions: Subscription[] = [];

    constructor(signaling: Signaling, key: CryptoKey) {
        log('FileReceiver: Constructor called');
        this.signaling = signaling;
        this.key = key;
        this.server = new ServerTransfer(signaling, key, {
            activate: () => {
                this.peerConnection?.close();
                this.dataChannel?.close();
                this.sendChannel?.close();
                this.transfers.clear();
                this.rejectBufferWaiters(
                    new Error('Connection changed. Please retry this file.'),
                );
                this.onConnectionStateChange?.('connected');
            },
            sendProgress: (id, progress, bytes) =>
                this.onSendProgress?.(id, progress, bytes),
            metadata: (id, metadata) => this.onMetadata?.(id, metadata),
            received: (id, file) => this.onFileReceived?.(id, file),
            receiveProgress: (id, progress, bytes) =>
                this.onProgress?.(id, progress, bytes),
            error: (id, err) => this.onError?.(id, err),
        });
        try {
            this.peerConnection =
                WebRTCConnectionFactory.createPeerConnection();
        } catch {
            signaling.suggestServer('webrtc_unavailable');
            return;
        }

        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                log('FileReceiver ICE candidate:', event.candidate.candidate);
                this.signaling.addIceCandidate(event.candidate);
            } else {
                log('FileReceiver: ICE gathering complete (null candidate)');
            }
        };

        this.peerConnection.onicecandidateerror = (event: Event) => {
            const err = event as RTCPeerConnectionIceErrorEvent;
            error('FileReceiver ICE ERROR:', {
                url: err.url,
                errorCode: err.errorCode,
                errorText: err.errorText,
            });
        };

        this.peerConnection.onicegatheringstatechange = () => {
            log(
                'FileReceiver ICE Gathering State:',
                this.peerConnection.iceGatheringState,
            );
        };

        this.peerConnection.oniceconnectionstatechange = () => {
            log(
                'FileReceiver ICE Connection State:',
                this.peerConnection.iceConnectionState,
            );
        };

        this.peerConnection.onconnectionstatechange = () => {
            if (this.disposed || this.signaling.serverEnabled) return;
            const state = this.peerConnection.connectionState;
            log('FileReceiver connection state:', state);
            if (state === 'connected') this.signaling.markConnected();
            else if (state === 'failed' || state === 'disconnected') {
                this.signaling.markDisconnected();
                if (state === 'failed')
                    this.signaling.suggestServer('connection_failed');
            }
            if (this.onConnectionStateChange) {
                this.onConnectionStateChange(state);
            }
        };

        this.subscriptions.push(
            this.signaling.iceCandidates$.subscribe((candidates) => {
                candidates.forEach((candidate) => {
                    const candidateStr = JSON.stringify(candidate);
                    if (this.processedCandidates.has(candidateStr)) {
                        return;
                    }
                    this.processedCandidates.add(candidateStr);

                    if (this.peerConnection.remoteDescription) {
                        this.peerConnection
                            .addIceCandidate(candidate)
                            .catch((e) =>
                                error('Error adding ICE candidate', e),
                            );
                    } else {
                        log('FileReceiver: Queuing ICE candidate');
                        this.candidateQueue.push(candidate);
                    }
                });
            }),
        );

        this.subscriptions.push(
            this.signaling.answer$.subscribe((answer) => {
                if (answer) {
                    this.setAnswer(answer);
                }
            }),
        );
    }

    public destroy() {
        this.disposed = true;
        this.server.destroy();
        this.rejectBufferWaiters(
            new Error('Connection interrupted while sending'),
        );
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
        if (this.sendChannel) {
            this.sendChannel.close();
            this.sendChannel = null;
        }
        if (this.peerConnection) {
            this.peerConnection.close();
        }
        if (this.dataChannel) {
            this.dataChannel.close();
            this.dataChannel = null;
        }
        this.transfers.clear();
        this.cancelledTransfers.clear();
        this.bufferWaiters = [];
        this.onProgress = null;
        this.onFileReceived = null;
        this.onConnectionStateChange = null;
        this.onMetadata = null;
        this.onError = null;
        this.onSendProgress = null;
    }

    public setCallbacks(
        onProgress: (
            id: number,
            progress: number,
            bytesReceived: number,
        ) => void,
        onFileReceived: (id: number, file: File) => void,
        onConnectionStateChange?: (state: RTCPeerConnectionState) => void,
        onMetadata?: (id: number, metadata: FileMetadata) => void,
        onError?: (id: number | null, error: Error) => void,
    ) {
        this.onProgress = onProgress;
        this.onFileReceived = onFileReceived;
        if (onConnectionStateChange) {
            this.onConnectionStateChange = onConnectionStateChange;
        }
        if (onMetadata) {
            this.onMetadata = onMetadata;
        }
        if (onError) {
            this.onError = onError;
        }
    }

    public setSendProgressCallback(
        callback: (id: number, progress: number, bytesSent: number) => void,
    ) {
        this.onSendProgress = callback;
    }

    public cancelTransfer(transferId: number) {
        if (this.server.active) {
            this.server.cancelTransfer(transferId);
            return;
        }
        log('FileReceiver: Cancelling send transfer', transferId);
        this.cancelledTransfers.add(transferId);
    }

    /**
     * Creates the offer with two data channels on the same WebRTC connection:
     * - fileTransfer: remote peer sends → we receive
     * - fileTransferSend: we send → remote peer receives
     * Single RTCPeerConnection used for bidirectional transfer.
     */
    public async createOffer(): Promise<RTCSessionDescriptionInit> {
        await this.signaling.announcePresence();
        if (!this.peerConnection)
            throw new Error('Direct connection is unavailable.');
        if (
            this.signaling.serverEnabled ||
            this.signaling.emulateSlow ||
            this.disposed
        )
            return { type: 'offer' };
        await WebRTCConnectionFactory.configureTurn(this.peerConnection);
        if (
            this.signaling.serverEnabled ||
            this.signaling.emulateSlow ||
            this.disposed
        )
            return { type: 'offer' };
        const attemptId = this.signaling.beginAttempt();
        this.dataChannel =
            this.peerConnection.createDataChannel('fileTransfer');
        this.setupDataChannel(this.dataChannel);

        this.sendChannel =
            this.peerConnection.createDataChannel('fileTransferSend');
        this.setupSendChannel(this.sendChannel);

        const offer = await this.peerConnection.createOffer();
        await this.peerConnection.setLocalDescription(offer);

        const finalOffer =
            this.peerConnection.localDescription?.toJSON() || offer;
        await this.signaling.setOffer(finalOffer, attemptId);
        return finalOffer;
    }

    public async setAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
        if (
            this.disposed ||
            this.signaling.serverEnabled ||
            this.signaling.emulateSlow
        )
            return;
        if (this.peerConnection.signalingState === 'closed') {
            log('FileReceiver: Signaling state is closed, skipping setAnswer');
            return;
        }

        if (
            this.peerConnection.remoteDescription ||
            this.peerConnection.signalingState === 'stable'
        ) {
            log(
                'FileReceiver: Remote description already set or stable, skipping setAnswer',
            );
            return;
        }

        log('FileReceiver: Setting remote description', answer);
        try {
            await this.peerConnection.setRemoteDescription(answer);

            // Process queued candidates
            while (this.candidateQueue.length > 0) {
                const candidate = this.candidateQueue.shift();
                if (candidate) {
                    log('FileReceiver: Adding queued ICE candidate', candidate);
                    this.peerConnection
                        .addIceCandidate(candidate)
                        .catch((e) =>
                            error('Error adding queued ICE candidate', e),
                        );
                }
            }
        } catch (err) {
            error('FileReceiver: Error setting remote description', err);
            this.signaling.suggestServer('answer_accept_failed');
        }
    }

    private setupDataChannel(channel: RTCDataChannel) {
        log('FileReceiver: setup data channel', channel);
        channel.onopen = () => {
            log('FileReceiver: Data channel opened');
        };
        channel.onclose = () => {
            if (this.disposed || this.signaling.serverEnabled) return;
            log('FileReceiver: Data channel closed');
            if (this.onConnectionStateChange) {
                this.onConnectionStateChange('disconnected');
            }
        };
        channel.onerror = (err) => {
            error('FileReceiver: Data channel error', err);
        };

        channel.onmessage = async (event) => {
            if (this.disposed || this.signaling.serverEnabled) return;
            const timestamp =
                new Date().toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                }) +
                '.' +
                new Date().getMilliseconds().toString().padStart(3, '0');
            log(`[${timestamp}] FileReceiver: Received message`, event.data);
            const data = event.data;

            // Handle metadata (JSON)
            if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    if (parsed.type === 'ping') {
                        log('FileReceiver: Ping received');
                        return;
                    }
                    if (parsed.type === 'metadata') {
                        const metadata: FileMetadata = {
                            id: parsed.id,
                            name: parsed.name,
                            size: parsed.size,
                            mimeType: parsed.mimeType,
                        };

                        try {
                            metadata.name = await CommonUtils.decryptString(
                                this.key,
                                metadata.name,
                            );
                        } catch (e) {
                            error(
                                'FileReceiver: Failed to decrypt filename',
                                e,
                            );
                            if (this.onError) {
                                this.onError(metadata.id, e as Error);
                            }
                        }

                        this.transfers.set(metadata.id, {
                            metadata,
                            receivedBuffers: [],
                            receivedSize: 0,
                            hasReportedError: false,
                        });

                        log(
                            'FileReceiver: Starting receive for',
                            metadata.name,
                            '(ID:',
                            metadata.id,
                            ')',
                        );
                        if (this.onMetadata) {
                            this.onMetadata(metadata.id, metadata);
                        }
                    }
                } catch (e) {
                    error('FileReceiver: Error parsing metadata', e);
                }
                return;
            }

            // Handle file chunks (ArrayBuffer)
            if (data instanceof ArrayBuffer) {
                if (data.byteLength < 4) return;

                const view = new DataView(data);
                const transferId = view.getUint32(0);
                const chunkData = data.slice(4);

                const state = this.transfers.get(transferId);
                if (!state) {
                    warn(
                        'FileReceiver: Received chunk for unknown transfer',
                        transferId,
                    );
                    return;
                }

                try {
                    const decryptStart = performance.now();
                    const decrypted = await CommonUtils.decryptChunk(
                        this.key,
                        chunkData,
                    );
                    const decryptEnd = performance.now();
                    const timestamp =
                        new Date().toLocaleTimeString('en-US', {
                            hour12: false,
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                        }) +
                        '.' +
                        new Date()
                            .getMilliseconds()
                            .toString()
                            .padStart(3, '0');
                    log(
                        `[${timestamp}] [ID:${transferId}] Chunk decrypted in ${(decryptEnd - decryptStart).toFixed(3)}ms`,
                    );

                    state.receivedBuffers.push(decrypted);
                    state.receivedSize += decrypted.byteLength;

                    const progress = Math.min(
                        100,
                        Math.round(
                            (state.receivedSize / state.metadata.size) * 100,
                        ),
                    );

                    if (this.onProgress) {
                        this.onProgress(
                            transferId,
                            progress,
                            state.receivedSize,
                        );
                    }

                    if (state.receivedSize >= state.metadata.size) {
                        this.finishFile(transferId);
                    }
                } catch (e) {
                    error('FileReceiver: Error decrypting chunk', e);
                    if (this.onError && !state.hasReportedError) {
                        this.onError(transferId, e as Error);
                        state.hasReportedError = true;
                    }
                }
            }
        };
        log('FileReceiver: finished setup data channel', channel);
    }

    private finishFile(transferId: number) {
        const state = this.transfers.get(transferId);
        if (!state) return;

        const mimeType = getMimeType(
            state.metadata.name,
            state.metadata.mimeType,
        );
        const blob = new Blob(state.receivedBuffers, {
            type: mimeType,
        });
        const file = new File([blob], state.metadata.name, {
            type: mimeType,
            lastModified: Date.now(),
        });

        log(
            'FileReceiver: File received complete',
            file,
            '(ID:',
            transferId,
            ')',
        );

        if (this.onFileReceived) {
            this.onFileReceived(transferId, file);
        }

        // Cleanup
        this.transfers.delete(transferId);
    }

    private setupSendChannel(channel: RTCDataChannel) {
        log('FileReceiver: setup send channel', channel);
        channel.bufferedAmountLowThreshold = BUFFER_THRESHOLD;
        channel.onopen = () => {
            log('FileReceiver: Send channel opened');
        };
        channel.onbufferedamountlow = () => {
            const waiters = [...this.bufferWaiters];
            this.bufferWaiters = [];
            waiters.forEach(({ resolve }) => resolve());
        };
        channel.onclose = () => {
            if (this.disposed || this.signaling.serverEnabled) return;
            log('FileReceiver: Send channel closed');
            this.rejectBufferWaiters(
                new Error('Connection interrupted while sending'),
            );
        };
        channel.onerror = () => {
            this.rejectBufferWaiters(
                new Error('Data channel reported an error while sending'),
            );
        };
    }

    private async waitForSendChannel(): Promise<RTCDataChannel> {
        if (this.sendChannel && this.sendChannel.readyState === 'open') {
            return this.sendChannel;
        }
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                clearInterval(intervalId);
                reject(new Error('Timeout waiting for send channel'));
            }, 30000);
            const intervalId = setInterval(() => {
                if (
                    this.sendChannel &&
                    this.sendChannel.readyState === 'open'
                ) {
                    clearTimeout(timeoutId);
                    clearInterval(intervalId);
                    resolve(this.sendChannel);
                }
            }, 200);
        });
    }

    private async waitForBufferSpace(): Promise<void> {
        const channel = this.requireOpenSendChannel();
        if (channel.bufferedAmount <= MAX_BUFFER_AMOUNT) {
            return;
        }
        return new Promise((resolve, reject) => {
            this.bufferWaiters.push({ resolve, reject });
        });
    }

    public async sendFile(file: File, transferId?: number): Promise<void> {
        if (this.server.active)
            return this.server.sendFile(
                file,
                transferId ?? Math.floor(Math.random() * 0xffffffff),
            );
        await this.waitForSendChannel();
        const id = transferId ?? Math.floor(Math.random() * 0xffffffff);
        const encryptedName = await CommonUtils.encryptString(
            this.key,
            file.name,
        );
        this.requireOpenSendChannel().send(
            JSON.stringify({
                type: 'metadata',
                id,
                name: encryptedName,
                size: file.size,
                mimeType: file.type,
            }),
        );

        let offset = 0;
        while (offset < file.size) {
            if (this.cancelledTransfers.has(id)) {
                this.cancelledTransfers.delete(id);
                throw new Error('CANCELLED');
            }
            await this.waitForBufferSpace();
            const buffer = await file
                .slice(offset, offset + CHUNK_SIZE)
                .arrayBuffer();
            await this.sendChunk(buffer, offset, file.size, id);
            offset += buffer.byteLength;
        }
    }

    private async sendChunk(
        buffer: ArrayBuffer,
        offset: number,
        totalSize: number,
        transferId: number,
    ) {
        const encrypted = await CommonUtils.encryptChunk(this.key, buffer);

        const result = new Uint8Array(4 + encrypted.byteLength);
        const view = new DataView(result.buffer);
        view.setUint32(0, transferId);
        result.set(new Uint8Array(encrypted), 4);

        this.requireOpenSendChannel().send(result.buffer);

        const progress = Math.min(
            100,
            Math.round(((offset + buffer.byteLength) / totalSize) * 100),
        );
        if (this.onSendProgress) {
            this.onSendProgress(
                transferId,
                progress,
                offset + buffer.byteLength,
            );
        }
    }

    private requireOpenSendChannel(): RTCDataChannel {
        if (!this.sendChannel || this.sendChannel.readyState !== 'open') {
            throw new Error(
                `Connection interrupted: data channel is ${this.sendChannel?.readyState ?? 'missing'}`,
            );
        }
        return this.sendChannel;
    }

    private rejectBufferWaiters(reason: Error) {
        const waiters = [...this.bufferWaiters];
        this.bufferWaiters = [];
        waiters.forEach(({ reject }) => reject(reason));
    }

    public async getConnectionType(): Promise<string> {
        if (this.server.active) return 'server';
        if (!this.peerConnection) return 'unknown';
        try {
            const stats = await this.peerConnection.getStats();
            let selectedPair: any = null;
            stats.forEach((report) => {
                if (
                    report.type === 'candidate-pair' &&
                    report.state === 'succeeded'
                ) {
                    selectedPair = report;
                }
            });

            if (selectedPair) {
                const localCandidate = stats.get(selectedPair.localCandidateId);
                const remoteCandidate = stats.get(
                    selectedPair.remoteCandidateId,
                );

                if (localCandidate && remoteCandidate) {
                    return `${localCandidate.candidateType || 'unknown'}-${remoteCandidate.candidateType || 'unknown'}`;
                }
            }
        } catch (e) {
            error('FileReceiver: Error getting connection type', e);
        }
        return 'unknown';
    }
}
